/**
 * SponsorPlaceholder Component
 * 
 * Reusable sponsorship placeholder for future lawyer partnerships.
 * Dark theme: Deep navy background with gold accents
 * 
 * Props:
 * - variant: "full" (clause pages), "hub" (hub pages), "disclosure" (homepage)
 * 
 * Future Configuration (update these when sponsor is added):
 * - SPONSOR_NAME: Name of the legal partner firm
 * - SPONSOR_URL: Website URL of the partner (currently "#" placeholder)
 */

// Dark theme color palette - extracted from ClauseClear logo
const colors = {
  background: '#2E334A',
  backgroundLight: '#3A3F54',
  text: '#FFFFFF',
  textMuted: '#B8BCC8',
  textDim: '#8A8F9E',
  accent: '#D4AF37',
  border: '#3E4459',
};

// Placeholder variables for future sponsor configuration
const SPONSOR_NAME = null; // e.g., "Smith & Associates Legal"
const SPONSOR_URL = "#"; // e.g., "https://example-law-firm.com.au"

export default function SponsorPlaceholder({ variant = "full" }) {
  // Homepage disclosure variant - discreet transparency notice
  if (variant === "disclosure") {
    return (
      <div 
        data-testid="sponsor-disclosure"
        className="mt-12 p-5 rounded-lg text-center" 
        style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
      >
        <p className="text-xs leading-relaxed" style={{ color: colors.textDim }}>
          ClauseClear may include sponsored recommendations to local legal professionals in the future.
        </p>
      </div>
    );
  }

  // Hub pages variant - footer-style notice
  if (variant === "hub") {
    return (
      <div 
        data-testid="sponsor-hub-placeholder"
        className="mt-12 p-6 rounded-lg text-center" 
        style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
      >
        <p className="text-sm leading-relaxed" style={{ color: colors.textDim }}>
          Professional advice available via local partners (coming soon)
        </p>
      </div>
    );
  }

  // Full variant for clause explanation pages
  return (
    <div 
      data-testid="sponsor-full-placeholder"
      className="mt-16 p-8 rounded-lg" 
      style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }}
    >
      <h3 
        className="text-lg font-medium mb-3" 
        style={{ color: colors.text, fontFamily: "'Fraunces', Georgia, serif" }}
      >
        Looking for professional advice?
      </h3>
      <p className="text-sm leading-relaxed mb-6" style={{ color: colors.textMuted }}>
        If this clause affects your situation, you may wish to speak with a qualified employment or contract lawyer.
      </p>
      <div className="pt-5" style={{ borderTop: `1px solid ${colors.border}` }}>
        <p className="text-xs mb-2" style={{ color: colors.textDim }}>
          Recommended Tasmanian legal partner (sponsorship)
        </p>
        {SPONSOR_NAME ? (
          <a 
            href={SPONSOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium hover:underline"
            style={{ color: colors.accent }}
          >
            {SPONSOR_NAME}
          </a>
        ) : (
          <p className="text-xs" style={{ color: colors.textDim }}>
            Partner details coming soon
          </p>
        )}
      </div>
    </div>
  );
}
